import { useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import ImageUploader, { ImageUploaderHandle } from "@/components/ImageUploader";
import Footer from "@/components/Footer";
import NavBar from "@/components/NavBar";
import SEOContent from "@/components/SEOContent";
import SEOHead from "@/components/SEOHead";
import BreadcrumbSEO from "@/components/BreadcrumbSEO";
import ExampleImages from "@/components/ExampleImages";
import { getToolBySlug } from "@/data/tools";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSiteStats } from "@/hooks/useSiteStats";
import { lazy, Suspense } from "react";

const AdSlot = lazy(() => import("@/components/AdSlot"));
const CompressionSlider = lazy(() => import("@/components/CompressionSlider"));

const featuredSlugs = ['redimensionar-imagen-pasaporte', 'redimensionar-imagen-tiktok', 'mejorar-foto-borrosa', 'comprimir-jpg', 'reducir-imagen-5mb', 'comprimir-imagen-para-cv'];
const featuredTools = featuredSlugs.map(s => getToolBySlug(s)!).filter(Boolean);

const toolCategories: Record<string, string> = {
  'redimensionar-imagen-pasaporte': 'Documentos',
  'redimensionar-imagen-tiktok':    'Redes sociales',
  'mejorar-foto-borrosa':           'Mejorar imagen',
  'comprimir-jpg':                  'Comprimir',
  'reducir-imagen-5mb':             'Reducir peso',
  'comprimir-imagen-para-cv':       'CV / Trabajo',
};

const Index = () => {
  const uploaderRef = useRef<ImageUploaderHandle>(null);
  const { trackEvent } = useAnalytics();
  const isMobile = useIsMobile();
  const { total } = useSiteStats();

  const scrollTracked = useRef({ s50: false, s75: false, s100: false });

  useEffect(() => {
    trackEvent('page_view', { page: '/', page_type: 'home', device_type: isMobile ? 'mobile' : 'desktop' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;
      const pct = (window.scrollY / scrollHeight) * 100;
      if (pct >= 50 && !scrollTracked.current.s50) {
        scrollTracked.current.s50 = true;
        trackEvent('scroll_50', { page: '/' });
      }
      if (pct >= 75 && !scrollTracked.current.s75) {
        scrollTracked.current.s75 = true;
        trackEvent('scroll_75', { page: '/' });
      }
      if (pct >= 100 && !scrollTracked.current.s100) {
        scrollTracked.current.s100 = true;
        trackEvent('scroll_100', { page: '/' });
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackEvent]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    if (section) {
      setTimeout(() => {
        const el = document.getElementById(section);
        if (el) {
          const navbarHeight = 64;
          window.scrollTo({ top: el.offsetTop - navbarHeight - 20, behavior: 'smooth' });
        }
        window.history.replaceState({}, '', '/');
      }, 300);
    }
  }, []);

  useEffect(() => {
    try {
      const referrer = document.referrer;
      if (referrer && new URL(referrer).origin === window.location.origin && referrer.includes('/blog')) {
        setTimeout(() => {
          document.getElementById('uploader-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 400);
      }
    } catch {
      // Invalid referrer URL — ignore
    }
  }, []);

  const handleExampleSelect = (file: File) => {
    uploaderRef.current?.addAndCompressFiles([file]);
  };

  const steps = [
    {
      number: "1",
      title: "Sube tus imágenes",
      description: "Arrastra y suelta tus imágenes JPEG o PNG, o haz clic para seleccionarlas"
    },
    {
      number: "2", 
      title: "Compresión automática",
      description: "La compresión comienza inmediatamente después de subir tus imágenes"
    },
    {
      number: "3",
      title: "Descarga",
      description: "Descarga tus imágenes optimizadas individual o grupalmente"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Comprimir Imágenes Online Gratis (JPG, PNG, WebP) | CompresorJPEG"
        description="Comprime y optimiza imágenes JPG, PNG, WebP gratis. Reduce hasta 90% del tamaño sin perder calidad. Herramienta rápida, segura y sin instalación."
        keywords="comprimir imagenes, compresor jpg, comprimir png, optimizar webp, reducir tamaño imagen, comprimir fotos online, optimizar imagenes web"
        canonical="https://compresorjpeg.com/"
      />
      
      <NavBar />
      
      <div className="container mx-auto px-4">
        <BreadcrumbSEO />
      </div>
      
      <header className="text-center pt-10 pb-6 animate-fade-in">
        <div className="container mx-auto px-4 max-w-2xl">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight tracking-tight">
            Comprime imágenes gratis,<br className="hidden md:block" /> sin instalar nada
          </h1>
          <p className="text-base md:text-lg text-muted-foreground mb-6">
            JPG, PNG y WebP — tus fotos no salen de tu dispositivo.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {['Sin registro', 'Privado — procesamos en tu navegador', '100% gratis'].map(label => (
              <span key={label} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                {label}
              </span>
            ))}
            {total !== null && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                {total.toLocaleString('es-ES')}+ optimizaciones realizadas
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 pb-8">
        <section className="animate-fade-in mb-3" aria-label="Herramienta de compresión de imágenes">
          <h2 className="sr-only">Compresor de Imágenes Online</h2>
          <ImageUploader ref={uploaderRef} />
        </section>

        <Suspense fallback={null}>
          <AdSlot position="result" className="max-w-3xl mx-auto mb-4" />
        </Suspense>

        <section className="animate-fade-in mb-10">
          <div className="text-center mb-6">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
              Ve la diferencia
            </p>
            <h2 className="text-xl font-semibold text-foreground">
              Ajusta la calidad y ve el ahorro en tiempo real
            </h2>
          </div>
          <Suspense fallback={<div className="h-64 bg-muted animate-pulse rounded-xl max-w-4xl mx-auto" />}>
            <CompressionSlider />
          </Suspense>
        </section>

        <section className="animate-fade-in mb-8" aria-label="Imágenes de ejemplo">
          <p className="text-xs font-medium text-muted-foreground text-center mb-3">
            Prueba sin subir nada — selecciona una imagen de ejemplo:
          </p>
          <ExampleImages onSelectExample={handleExampleSelect} compact />
        </section>

        <section className="animate-fade-in mb-10">
          <p className="text-xs font-medium text-muted-foreground text-center uppercase tracking-widest mb-4">
            Casos de uso frecuentes
          </p>
          <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
            {[
              { label: 'Formulario oficial',  to: '/herramientas/comprimir-imagen-para-formulario' },
              { label: 'CV y trabajo',        to: '/herramientas/comprimir-imagen-para-cv' },
              { label: 'WhatsApp',            to: '/herramientas/comprimir-imagen-para-whatsapp' },
              { label: 'Instagram',           to: '/herramientas/comprimir-imagen-para-instagram' },
              { label: 'Foto pasaporte',      to: '/herramientas/redimensionar-imagen-pasaporte' },
              { label: 'TikTok',              to: '/herramientas/redimensionar-imagen-tiktok' },
              { label: 'LinkedIn',            to: '/herramientas/comprimir-imagen-para-linkedin' },
              { label: 'Email',               to: '/herramientas/comprimir-imagen-para-email' },
            ].map(({ label, to }) => (
              <Link
                key={to}
                to={to}
                className="inline-flex items-center px-4 py-2 rounded-full border border-border bg-card text-sm text-foreground/70 hover:text-primary hover:border-primary/30 hover:bg-primary/5 transition-all duration-200"
              >
                {label}
              </Link>
            ))}
          </div>
        </section>

        <section id="how-it-works-section" className="text-center mb-16 animate-fade-in">
          <h2 className="text-2xl font-bold text-foreground mb-10">
            ¿Cómo comprimir imágenes online?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <article key={index} className="text-center">
                <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-3 text-base font-bold">
                  {step.number}
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {step.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        <Suspense fallback={null}>
          <AdSlot position="inline" className="max-w-3xl mx-auto mb-12" />
        </Suspense>

        <section className="animate-fade-in mb-16" aria-label="Herramientas populares">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Herramientas populares
            </h2>
            <p className="text-sm text-muted-foreground">
              Comprime, convierte y redimensiona imágenes para cualquier uso
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {featuredTools.map(tool => (
              <Link
                key={tool.slug}
                to={`/herramientas/${tool.slug}`}
                className="group p-5 rounded-xl border border-border bg-card hover:shadow-md hover:border-primary/30 transition-all duration-200"
              >
                <p className="text-xs font-medium text-primary/60 mb-1.5 uppercase tracking-wide">
                  {toolCategories[tool.slug]}
                </p>
                <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors mb-1">
                  {tool.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {tool.subtitle}
                </p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link
              to="/herramientas"
              className="inline-flex items-center text-sm font-medium text-primary hover:underline"
            >
              Ver todas las herramientas →
            </Link>
          </div>
        </section>

        <section id="ventajas-section" className="animate-fade-in mb-16">
          <SEOContent showOnlyFirstSections={true} />
        </section>

        <section id="caracteristicas-section" className="mb-16 animate-fade-in">
          <SEOContent showOnlyFirstSections={false} />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
